import React from 'react';
import PageContainer from 'containers/PageContainer';
import { useDispatch } from 'react-redux';
import { mapPack, findNextAndPrevPack } from 'helpers/subscriptionHelpers';
import { useHistory, useRouteMatch, useLocation } from 'react-router';
import OrderSkipView from 'views/OrderSkipView';
import { skipPack } from 'gateways/Api';
import useMessage from 'hooks/useMessages';
import moment from 'moment';
import { resetSubscriptionsList } from 'store/actions/subscription';
import { setAppBusy } from 'store/actions/system';
import useNavHistory from 'hooks/useNavHistory';
import AddressSetView from 'views/AddressSetView';
import queryString from 'query-string';
import useSaveAddress from 'hooks/useSaveAddress';
import OrderView from 'views/OrderView';
import OrdersPageView from 'views/OrdersPageView';
import dtHelper from 'helpers/dateTimeHelper';

const OrdersContainer = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { genericMessage, genericFailureMessage } = useMessage();
  const { navigateToClosestRoot } = useNavHistory();
  const today = moment(dtHelper.now()).format('YYYY-MM-DD');
  const search = queryString.parse(location.search);
  const saveAddress = useSaveAddress();

  const handleSkip = (subscription, pack) => {
    dispatch(setAppBusy(true));
    skipPack(subscription.id, pack.id)
      .then(() => genericMessage(
        `Thank you, you have successfully skipped your ${moment(
          pack.deliveryDate,
        ).format(
          'MMMM Do',
        )} delivery. Any credit we owe you will be used towards your future payments.`,
        'info',
      ))
      .then(navigateToClosestRoot)
      .then(() => dispatch(resetSubscriptionsList()))
      .catch(() => {
        genericFailureMessage();
      })
      .finally(() => dispatch(setAppBusy(false)));
  };

  const changeAddress = (
    address,
    makePrimary,
    applyToAll,
    subscription,
    pack,
  ) => {
    saveAddress({
      address,
      subscription,
      pack,
      onSuccess: history.goBack,
      applyToAll,
      makePrimary,
    });
  };

  const viewMap = {
    list: (subscription, account, _pack, store) => {
      const packs = subscription.packs
        .sort((pack1, pack2) => moment(pack1.delivery_date).diff(pack2.delivery_date))
        .reduce((accum, pack) => {
          if (moment(pack.delivery_date).isBefore()) {
            accum.previousPacks.unshift(mapPack(pack, subscription, account, store));
          } else {
            accum.upcomingPacks.push(mapPack(pack, subscription, account, store));
          }
          return accum;
        }, { previousPacks: [], upcomingPacks: [] });
      return <OrdersPageView {...packs} />;
    },
    view: (subscription, account, pack) => {
      if (!pack) {
        return null;
      }
      const { nextDeliverablePack } = findNextAndPrevPack(
        subscription.packs,
        today,
      );
      const canChangeAddress = !subscription.isNonLensMail && !account.isMinor;

      const showJustMoved = canChangeAddress
        && pack.status === 'Pending'
        && (
          nextDeliverablePack?.id === pack.id
          || (nextDeliverablePack?.nextPack?.id === pack.id && nextDeliverablePack?.status !== 'Pending')
        );

      return (
        <OrderView
          pack={pack}
          canChangeAddress={canChangeAddress}
          showJustMoved={showJustMoved}
        />
      );
    },
    skip: (subscription, account, pack) => pack && (
      <OrderSkipView
        onSkip={() => handleSkip(subscription, pack)}
        onCancel={() => history.goBack()}
        pack={pack}
      />
    ),
    changeAddress: (subscription, account, pack) => pack && (
      <AddressSetView
        onSubmit={(address, makePrimary, applyToAll) => (
          changeAddress(address, makePrimary, applyToAll, subscription, pack)
        )}
        addresses={account.address_book}
        currentAddressId={pack.deliveryAddress.id}
        enableApplyToAll={!!search.enableApplyToAll}
        defaultApplyToAll={!!search.defaultApplyToAll}
        enablePrimary={!!search.enablePrimary}
        defaultPrimary={!!search.defaultPrimary}
      />
    ),
  };

  const { orderId } = match.params;

  function render(subscription, account, store) {
    const action = match.params.action || (orderId && 'view') || 'list';
    const rawPack = orderId && subscription.packs.find((p) => p.id === orderId);
    const pack = mapPack(rawPack, subscription, account, store);
    return viewMap[action](subscription, account, pack, store);
  }

  return (
    <PageContainer
      requiresAccount
      requiresSubscription
      hasBack={!!orderId}
      displayNavBar={!orderId}
      render={render}
    />
  );
};

OrdersContainer.displayName = 'OrdersContainer';

export default OrdersContainer;
